import { useEffect, useState } from 'react';
import { applicationDocsApi } from '@/services/applicationDocs/api';
import { isHandledAuthError } from '@/services/client';
import { downloadBlob } from '@/utils/download';
import { japaneseNameDisplay } from '@/utils/applicationDoc';
import { normalizeApplicationBankAccountType } from '@/utils/bankAccountType';
import { formatDateTime } from '@/utils/date';
import { formatPastAddressPeriodLabel } from '@/utils/pastAddressPeriod';
import {
  APP_DOC_ATTACHMENT_GROUPS,
  appDocAddonMeta,
  attachmentFilesOf,
  attachmentLabel,
  evidenceKeyForAddition,
  formatAddonPeriodLabel,
  isMultiFileAttachmentKey,
  mergeAppDocAttachments,
} from './appDocContent';
import type { AppDocDataLike } from '@/pages/application/ApplicationDocDetail';

type AppDocLike = AppDocDataLike;

interface AppDocContentDisplayProps {
  data: AppDocLike;
  hideMemo?: boolean;
}

/** 見出し付きカード。no を渡すと番号バッジ、無ければ青いアクセントバー。 */
const SectionCard = ({ no, title, children }: { no?: string | number; title: string; children: React.ReactNode }) => (
  <section className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200/70 overflow-hidden">
    <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50/70 via-white to-white">
      {no != null ? (
        <span className="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 text-white text-sm font-bold shadow-sm">{no}</span>
      ) : (
        <span className="inline-block w-1.5 h-5 rounded-full bg-blue-600 shrink-0" />
      )}
      <h3 className="text-base font-bold text-gray-800 leading-snug">{title}</h3>
    </div>
    <div className="p-5 md:p-6">{children}</div>
  </section>
);

/** ラベル上・値下のフィールド。値が空のときは淡色の「—」。 */
const InfoField = ({ label, value, children, full, mono }: { label: string; value?: unknown; children?: React.ReactNode; full?: boolean; mono?: boolean }) => (
  <div className={`rounded-lg bg-gray-50/70 ring-1 ring-gray-100 px-3.5 py-2.5 ${full ? 'sm:col-span-2' : ''}`}>
    <dt className="text-xs font-medium text-gray-400 mb-0.5">{label}</dt>
    <dd className={`text-sm font-normal text-gray-900 break-words ${mono ? 'font-mono' : ''}`}>
      {children != null ? children : (value != null && String(value).trim() !== '' ? String(value) : <span className="text-gray-300">—</span>)}
    </dd>
  </div>
);

const phoneDigitsOnly = (value: unknown) => String(value ?? '').replace(/-/g, '').replace(/\D/g, '');

const AppDocContentDisplay = ({ data, hideMemo = false }: AppDocContentDisplayProps) => {
  const [preview, setPreview] = useState<{
    url: string;
    label: string;
    fileName: string;
    contentType: string;
    uploadedAt?: string;
  } | null>(null);
  const d: AppDocLike = data || {};
  const attachments = mergeAppDocAttachments(d.attachments);
  const members: Record<string, string>[] = Array.isArray(d.members) && d.members.length ? (d.members as Record<string, string>[]) : [];
  const pastAddresses: Record<string, string>[] = Array.isArray(d.pastAddresses) ? (d.pastAddresses as Record<string, string>[]) : [];
  const others: Record<string, string>[] = Array.isArray(d.otherHouseholdMembers) ? (d.otherHouseholdMembers as Record<string, string>[]) : [];
  const additions: Record<string, string>[] = Array.isArray(d.additionRows) ? (d.additionRows as Record<string, string>[]) : [];
  const bank: Record<string, string> = d.bank || {};
  const residenceSameAsNow = d.protectionResidenceSame === false || d.protectionResidenceSame === 'different' ? false : true;
  const sameLabel = residenceSameAsNow ? '現在と同じ' : '異なる';
  const displayValue = (value: unknown) => (value != null && String(value).trim() !== '' ? String(value) : '—');

  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
  }, [preview]);

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const handleAttachmentReference = async (key: string, fileIndex = 0) => {
    const file = attachmentFilesOf(attachments[key])[fileIndex];
    if (!d.id || !file?.fileName) return;
    try {
      const response = await applicationDocsApi.downloadAttachment(Number(d.id), key, fileIndex);
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: file.contentType || 'application/octet-stream' });
      const contentType = blob.type || file.contentType || 'application/octet-stream';
      const url = URL.createObjectURL(blob);
      if (contentType.startsWith('image/') || contentType === 'application/pdf') {
        if (preview?.url) URL.revokeObjectURL(preview.url);
        setPreview({
          url,
          label: attachmentLabel(key),
          fileName: file.fileName,
          contentType,
          uploadedAt: file.uploadedAt,
        });
      } else {
        downloadBlob(blob, file.fileName);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      if (isHandledAuthError(error)) return;
    }
  };

  /**
   * 参照用の共通ファイルチップ（加算等・添付書類で共通利用）。
   * クリック時は既存の handleAttachmentReference（オンデマンド取得→プレビュー/DL）を呼ぶ。
   */
  const FilePreviewChip = ({ fileName, contentType, attachmentKey, fileIndex }: { fileName: string; contentType?: string; attachmentKey: string; fileIndex: number }) => {
    const isPdf = /\.pdf$/i.test(fileName) || contentType === 'application/pdf';
    return (
      <button
        type="button"
        disabled={!d.id}
        onClick={() => void handleAttachmentReference(attachmentKey, fileIndex)}
        title={fileName}
        className={`group inline-flex items-center gap-2 max-w-full rounded-lg border border-gray-200 bg-white py-1.5 pl-1.5 pr-2.5 shadow-sm transition ${d.id ? 'hover:border-blue-400 hover:bg-blue-50/50 hover:shadow cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
      >
        <span className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md text-[10px] font-bold tracking-tight ${isPdf ? 'bg-red-50 text-red-500' : 'bg-blue-50 text-blue-500'}`} aria-hidden>
          {isPdf ? 'PDF' : 'IMG'}
        </span>
        <span className="font-mono text-xs text-gray-700 min-w-0 max-w-[16rem] truncate">{fileName}</span>
        <span className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 group-hover:text-blue-700">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
          参照
        </span>
      </button>
    );
  };

  const renderAttachmentPart = (key: string, partLabel?: string) => {
    const allFiles = attachmentFilesOf(attachments[key]);
    const files = isMultiFileAttachmentKey(key) ? allFiles : allFiles.slice(0, 1);
    const has = files.length > 0;
    return (
      <div key={key} className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
        {partLabel ? <p className="text-xs text-gray-500 mb-1.5">{partLabel}</p> : null}
        {has ? (
          <div className="flex flex-col items-start gap-2">
            {files.map((file, idx) => (
              <FilePreviewChip
                key={`${key}-${idx}-${file.fileName}`}
                fileName={file.fileName}
                contentType={file.contentType}
                attachmentKey={key}
                fileIndex={idx}
              />
            ))}
          </div>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
            <span className="inline-block w-2 h-2 rounded-full bg-gray-200" />未添付
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <SectionCard title="申出者情報">
        {/* 識別ID・申請ID・世帯番号・ステータスはヒーローヘッダーに表示済み。
            ここで再掲すると二重表示になるため、プロトタイプ version2 に合わせて省く。 */}
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InfoField label="申出年月日" value={d.applicationDate} />
          <InfoField label="電話番号" value={phoneDigitsOnly(d.applicantPhone)} mono />
          <InfoField label="現住所" value={d.currentAddress} full />
          <InfoField label="フリガナ" value={japaneseNameDisplay(d.applicantFurigana || members[0]?.furigana)} />
          <InfoField label="代表者氏名（表示用）" value={japaneseNameDisplay(d.applicantName ?? d.name)} />
          <InfoField label="生年月日" value={d.applicantBirthDate} />
          <InfoField label="メールアドレス" value={d.applicantEmail} full />
        </dl>
      </SectionCard>

      <SectionCard no="1" title="世帯構成（申出者及び世帯員　1～8名）">
        <div className="space-y-3">
          {members.map((m, i) => {
            const rel = m.relationToHead === 'その他' && m.relationToHeadOther
              ? `その他（${m.relationToHeadOther}）`
              : (m.relationToHead === '本人' ? '世帯主本人' : (m.relationToHead || ''));
            return (
              <div key={i} className="rounded-xl bg-gray-50/60 ring-1 ring-gray-200 p-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-white ring-1 ring-gray-200 text-xs font-bold text-gray-500">{i + 1}</span>
                  <span className="text-sm font-semibold text-gray-900">{displayValue(japaneseNameDisplay(m.name))}</span>
                  {rel ? <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700">{rel}</span> : null}
                  {m.death === '有' ? <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-red-50 text-red-600">死亡</span> : null}
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InfoField label="フリガナ" value={japaneseNameDisplay(m.furigana)} />
                  <InfoField label="氏名" value={japaneseNameDisplay(m.name)} />
                  <InfoField label="世帯主との続柄" value={rel} />
                  <InfoField label="生年月日" value={m.birthDate} />
                  <InfoField label="生活保護受給期間" value={m.benefitPeriod} full />
                </dl>
              </div>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard no="2" title="保護を受給していた当時の居所">
        <p className="text-sm text-gray-800">現住所と <span className="inline-block px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold">{sameLabel}</span></p>
        <div className="mt-4 pt-4 border-t border-dashed border-gray-200">
          <p className="text-xs font-semibold text-gray-400 mb-2">当時の居所・期間（最大3件）</p>
          {residenceSameAsNow ? (
            <p className="text-sm text-gray-500 bg-gray-50 ring-1 ring-gray-100 rounded-lg px-4 py-3">「現住所と同じ」のため、当時の居所の登録はありません。</p>
          ) : pastAddresses.length === 0 ? (
            <p className="text-sm text-gray-400">登録なし</p>
          ) : (
            <div className="space-y-3">
              {pastAddresses.map((p, i) => (
                <div key={i} className="rounded-lg bg-gray-50/70 ring-1 ring-gray-100 px-4 py-3">
                  <div className="mb-2 inline-flex items-center justify-center w-6 h-6 rounded-md bg-white ring-1 ring-gray-200 text-xs font-bold text-gray-500">{i + 1}</div>
                  <dl className="grid grid-cols-1 gap-2 sm:grid-cols-[7rem_1fr] text-sm">
                    <dt className="text-gray-400 text-xs font-medium">当時の居所</dt>
                    <dd className="text-gray-900">{p.address || '—'}</dd>
                    <dt className="text-gray-400 text-xs font-medium">期間</dt>
                    <dd className="text-gray-900">{formatPastAddressPeriodLabel(p.periodStartMonth, p.periodEndMonth, p.period) || '—'}</dd>
                  </dl>
                </div>
              ))}
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard no="3" title="振込先口座">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InfoField label="銀行番号" value={bank.bankCode} mono />
          <InfoField label="支店番号" value={bank.bankBranchCode} mono />
          <InfoField label="預金種目" value={normalizeApplicationBankAccountType(bank.accountType)} />
          <InfoField label="口座番号" value={bank.accountNumber} mono />
          <InfoField label="口座名義" value={bank.accountHolderKana} mono full />
        </dl>
      </SectionCard>

      <SectionCard no="4" title="申出者以外の世帯員の連絡先">
        {others.length === 0 ? (
          <p className="text-sm text-gray-400">登録なし</p>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm rounded-lg overflow-hidden ring-1 ring-gray-200">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold tracking-wide border-b border-gray-200">氏名</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold tracking-wide border-b border-gray-200">電話番号</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {others.map((o, i) => (
                  <tr key={i} className="hover:bg-blue-50/40 transition-colors">
                    <td className="px-4 py-3 text-gray-900">{displayValue(japaneseNameDisplay(o.name))}</td>
                    <td className="px-4 py-3 font-mono text-gray-700">{displayValue(phoneDigitsOnly(o.phone))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard no="5" title="加算等の申出">
        {additions.length === 0 ? (
          <p className="text-sm text-gray-400">該当なし</p>
        ) : (
          <div className="space-y-3 text-sm">
            {additions.map((a, i) => {
              const evidenceKey = evidenceKeyForAddition(a, i, attachments);
              const evidenceFiles = evidenceKey ? attachmentFilesOf(attachments[evidenceKey]) : [];
              const meta = appDocAddonMeta(a.category || '');
              const periodLabel = formatAddonPeriodLabel(a.periodStartMonth, a.periodEndMonth);
              return (
                <div key={i} className="rounded-lg bg-gray-50/70 ring-1 ring-gray-100 px-4 py-3">
                  <div className="mb-2 inline-flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-white ring-1 ring-gray-200 text-xs font-bold text-gray-500">{i + 1}</span>
                    <span className="font-bold text-gray-800">{a.category || '—'}</span>
                  </div>
                  <dl className="grid grid-cols-1 gap-2 sm:grid-cols-[7rem_1fr]">
                    <dt className="text-gray-400 text-xs font-medium">氏名</dt>
                    <dd className="text-gray-900">{a.name || '—'}</dd>
                    <dt className="text-gray-400 text-xs font-medium">期間</dt>
                    <dd className="text-gray-900">{periodLabel || '—'}</dd>
                    {meta.extraLabel ? (
                      <>
                        <dt className="text-gray-400 text-xs font-medium">{meta.extraLabel}</dt>
                        <dd className="text-gray-900">{a.facility || '—'}</dd>
                      </>
                    ) : null}
                    <dt className="text-gray-400 text-xs font-medium">挙証資料</dt>
                    <dd className="text-gray-900">
                      {evidenceFiles.length && evidenceKey ? (
                        <div className="flex flex-col items-start gap-2">
                          {evidenceFiles.map((file, idx) => (
                            <FilePreviewChip
                              key={`${evidenceKey}-${idx}-${file.fileName}`}
                              fileName={file.fileName}
                              contentType={file.contentType}
                              attachmentKey={evidenceKey}
                              fileIndex={idx}
                            />
                          ))}
                        </div>
                      ) : a.evidenceFileName ? (
                        <span className="font-mono text-xs text-gray-500 break-all">{a.evidenceFileName}</span>
                      ) : (
                        <span className="text-gray-300 font-normal">未添付</span>
                      )}
                    </dd>
                  </dl>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <SectionCard title="添付書類（証明書等）">
        <p className="text-sm text-gray-600 mb-2">提出のあった添付のみファイル名が表示されます。必要なときに「参照」で内容を確認できます。</p>
        <p className="text-xs text-gray-500 mb-4">※ 本人確認書類（マイナンバーカード／運転免許証／在留カード／障害者手帳／パスポート）は、いずれか1点を添付してください（すべて必須ではありません）。</p>
        <div className="space-y-3">
          {APP_DOC_ATTACHMENT_GROUPS.map((group, gi) => {
            const firstIdDoc = group.idDoc && (gi === 0 || !APP_DOC_ATTACHMENT_GROUPS[gi - 1]?.idDoc);
            // legacyOnly（マイナンバー裏面）は既存添付があるときのみ表示
            const parts = group.parts.filter((part) => !part.legacyOnly || attachmentFilesOf(attachments[part.key]).length > 0);
            return (
              <div key={group.label}>
                {firstIdDoc ? (
                  <p className="text-xs font-semibold text-gray-500 mb-2">本人確認書類（いずれか1点）</p>
                ) : null}
                <div className="rounded-lg border border-gray-200 bg-gray-50/40 p-3.5">
                  <p className="text-sm font-semibold text-gray-800 leading-relaxed">{group.label}</p>
                  {group.note ? <p className="text-xs text-gray-500 mt-1 leading-relaxed whitespace-pre-line">{group.note}</p> : null}
                  <div className={`mt-2 grid gap-3 ${parts.length > 1 ? 'sm:grid-cols-2' : ''}`}>
                    {parts.map((part) => renderAttachmentPart(part.key, part.label))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* ⑦ 申出書 OCR で使用した原本ファイル（確認登録時にシステムがコピー。閲覧専用）。存在するときのみ表示。 */}
      {attachmentFilesOf(attachments['moushideshoOriginal']).length > 0 ? (
        <SectionCard title="OCR 読取原本（申請書）">
          <p className="text-sm text-gray-600 mb-2">OCR 読取に使用した申請書の原本です。「参照」で内容を確認できます（閲覧専用）。</p>
          {renderAttachmentPart('moushideshoOriginal', '申請書 原本')}
        </SectionCard>
      ) : null}

      {!hideMemo && d.memo ? (
        <SectionCard title="備考">
          <p className="text-sm text-gray-700 bg-gray-50 ring-1 ring-gray-100 rounded-lg p-3 whitespace-pre-wrap">{d.memo}</p>
        </SectionCard>
      ) : null}

      {preview && (
        <div
          className="fixed inset-0 z-[60] flex cursor-pointer items-center justify-center p-4 bg-black/50"
          onClick={closePreview}
          role="presentation"
        >
          <div
            className="cursor-default bg-white rounded-xl shadow-2xl max-w-3xl w-full h-[92vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="att-preview-title"
          >
            <div className="shrink-0 px-6 pt-5 pb-3 border-b border-gray-100">
              <h3 id="att-preview-title" className="text-lg font-bold text-gray-800">{preview.label}</h3>
              <p className="text-sm text-gray-600 font-mono break-all mt-1">{preview.fileName}</p>
              {preview.uploadedAt ? (
                <p className="text-xs text-gray-500 mt-0.5">アップロード日時: {formatDateTime(preview.uploadedAt)}</p>
              ) : null}
            </div>
            <div className="flex-1 min-h-0 bg-gray-100">
              {preview.contentType === 'application/pdf' ? (
                <iframe title={preview.fileName} src={`${preview.url}#view=Fit&toolbar=1&navpanes=0`} className="w-full h-full border-0 block" />
              ) : preview.contentType.startsWith('image/') ? (
                <div className="w-full h-full overflow-auto flex items-center justify-center p-3">
                  <img src={preview.url} alt={preview.fileName} className="max-w-full max-h-full h-auto rounded-lg shadow" />
                </div>
              ) : null}
            </div>
            <div className="shrink-0 px-6 py-4 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={closePreview}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium cursor-pointer"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppDocContentDisplay;
